// SEO Content Data for Location Pages
export const locationPages = [
  {
    slug: "capitol-hill",
    title: "Vacation Rentals Near Capitol Hill",
    metaTitle: "Vacation Rentals Near Capitol Hill | Capitol View Stays",
    metaDescription: "Book premium vacation rentals near Capitol Hill in Washington DC. Fully furnished homes with fast WiFi, self check-in & prime locations. Save 15% booking direct!",
    keywords: "Capitol Hill vacation rental, vacation rentals near Capitol Hill DC, short term rentals Capitol Hill",
    heroImage: "https://images.unsplash.com/photo-1617581629397-a72507c3de9e?w=1200",
    intro: "Looking for the perfect vacation rental near Capitol Hill? Capitol View Stays offers premium, fully-furnished short-term rentals just steps from the United States Capitol, Supreme Court, and Library of Congress. Our properties provide the ideal home base for exploring Washington DC's most historic neighborhood.",
    attractions: [
      { name: "United States Capitol", distance: "0.3 miles", description: "The iconic seat of the U.S. Congress and one of America's most recognizable landmarks." },
      { name: "Library of Congress", distance: "0.4 miles", description: "The largest library in the world, featuring stunning architecture and free exhibitions." },
      { name: "Supreme Court", distance: "0.3 miles", description: "The highest court in the land, open for public tours when in session." },
      { name: "Eastern Market", distance: "0.5 miles", description: "A vibrant weekend market with local vendors, fresh produce, and artisan crafts." },
      { name: "Capitol Hill Restaurants", distance: "Walking distance", description: "Dozens of acclaimed restaurants, cafes, and bars along Pennsylvania Avenue and Barracks Row." }
    ],
    whyStay: [
      "Walk to the Capitol, Supreme Court, and Library of Congress in minutes",
      "Experience authentic DC neighborhood life with local shops and restaurants",
      "Easy Metro access via Capitol South and Eastern Market stations",
      "Safe, tree-lined residential streets perfect for families",
      "Central location for exploring all of Washington DC"
    ],
    content: `
## Why Choose a Vacation Rental Near Capitol Hill?

Capitol Hill is Washington DC's most historic and charming neighborhood, offering visitors an authentic experience of life in the nation's capital. Unlike staying in a downtown hotel, our Capitol Hill vacation rentals put you in the heart of a real DC neighborhood where senators, congressional staffers, and longtime residents call home.

### The Perfect Location for Sightseeing

When you stay near Capitol Hill with Capitol View Stays, you're within walking distance of Washington DC's most important landmarks. Wake up and stroll to the United States Capitol for a morning tour, grab lunch at Eastern Market, and spend your afternoon exploring the Smithsonian museums on the National Mall—all without needing a car or taxi.

Our Capitol Hill properties are located just blocks from:
- The United States Capitol Building
- The Supreme Court of the United States
- The Library of Congress
- Folger Shakespeare Library
- Eastern Market
- Barracks Row restaurants and shops

### More Space, More Comfort, More Savings

Unlike cramped hotel rooms, our Capitol Hill vacation rentals offer full kitchens, multiple bedrooms, private laundry, and comfortable living spaces. Traveling with family or a group? Our properties sleep 6-11 guests, making them perfect for multi-generational trips, friend getaways, or corporate retreats.

**Save 15% when you book directly with Capitol View Stays** instead of through third-party booking sites. Plus, enjoy amenities you won't find in hotels:
- Full gourmet kitchens
- High-speed WiFi for remote work
- Washer and dryer in-unit
- Private outdoor spaces
- Self check-in for flexible arrivals

### Getting Around from Capitol Hill

Capitol Hill's central location makes it easy to explore all of Washington DC:

**By Metro:** Capitol South Station (Orange, Blue, Silver lines) and Eastern Market Station (Orange, Blue, Silver lines) are both within a 5-minute walk, connecting you to the entire DC Metro system.

**By Foot:** The National Mall, Smithsonian museums, and downtown DC are all walkable from Capitol Hill—perfect for visitors who want to maximize their sightseeing time.

**By Bike:** Capital Bikeshare stations are located throughout the neighborhood, offering an affordable and fun way to explore the city.

### Ideal for Every Type of Traveler

**Families:** Our spacious rentals give kids room to spread out, while the neighborhood's parks, Eastern Market, and proximity to the National Zoo make it perfect for family vacations.

**Business Travelers:** With dedicated workspaces, fast WiFi, and easy access to Capitol Hill offices and downtown, our rentals are ideal for extended business trips.

**Groups:** Traveling with friends or colleagues? Our larger properties accommodate groups comfortably, with shared living spaces for gathering and private bedrooms for rest.

### Book Your Capitol Hill Vacation Rental Today

Ready to experience Washington DC like a local? Browse our Capitol Hill vacation rentals and book directly to save 15% on your stay. Our team is here to help you find the perfect property for your DC adventure.
    `,
    nearbyLocations: ["national-mall", "eastern-market", "union-station", "navy-yard"]
  },
  {
    slug: "national-mall",
    title: "Vacation Rentals Near the National Mall",
    metaTitle: "Vacation Rentals Near National Mall DC | Capitol View Stays",
    metaDescription: "Stay near the National Mall in Washington DC. Premium vacation rentals close to Smithsonian museums, monuments & memorials. Book direct & save 15%!",
    keywords: "vacation rentals near National Mall, National Mall DC rentals, short term rentals near Smithsonian",
    heroImage: "https://images.unsplash.com/photo-1617581629397-a72507c3de9e?w=1200",
    intro: "Experience the heart of America with a vacation rental near the National Mall. Capitol View Stays offers premium accommodations within walking distance of the Smithsonian museums, Washington Monument, Lincoln Memorial, and more. Our fully-furnished rentals provide the perfect base for exploring DC's iconic landmarks.",
    attractions: [
      { name: "Smithsonian Museums", distance: "0.5-1 mile", description: "19 world-class museums and galleries, all with free admission." },
      { name: "Washington Monument", distance: "0.8 miles", description: "The iconic 555-foot obelisk honoring George Washington." },
      { name: "Lincoln Memorial", distance: "1.5 miles", description: "The majestic memorial to America's 16th president." },
      { name: "World War II Memorial", distance: "1 mile", description: "A stunning tribute to the Greatest Generation." },
      { name: "National Gallery of Art", distance: "0.6 miles", description: "One of the world's finest art collections, free to visit." }
    ],
    whyStay: [
      "Walk to all Smithsonian museums and monuments",
      "Perfect for families visiting DC for the first time",
      "Central location minimizes transportation costs",
      "Experience more attractions with saved commute time",
      "Safe, well-lit neighborhood perfect for evening strolls"
    ],
    content: `
## Why Stay Near the National Mall?

The National Mall is the crown jewel of Washington DC, stretching two miles from the United States Capitol to the Lincoln Memorial. When you choose a Capitol View Stays vacation rental near the National Mall, you're positioning yourself at the center of America's most significant monuments, memorials, and museums.

### Walk to World-Class Museums

The Smithsonian Institution operates 19 museums along and around the National Mall—all completely free to visit. From the National Air and Space Museum to the National Museum of African American History and Culture, you could spend weeks exploring these incredible institutions.

Our vacation rentals near the National Mall put you within walking distance of:
- National Air and Space Museum
- National Museum of Natural History
- National Museum of American History
- National Museum of African American History and Culture
- National Gallery of Art (East and West Buildings)
- Hirshhorn Museum and Sculpture Garden
- And many more!

### Monuments & Memorials at Your Doorstep

Imagine starting your morning with a sunrise walk to the Lincoln Memorial, or ending your day watching the sunset behind the Washington Monument. When you stay near the National Mall with Capitol View Stays, these magical moments become part of your daily experience.

### Save Time and Money

Staying near the National Mall eliminates the need for expensive taxis, rideshares, or parking fees. Our guests typically save $50-100 per day on transportation alone—savings that add up quickly during a week-long vacation.

**Book directly with Capitol View Stays and save an additional 15%** compared to third-party booking sites. Our vacation rentals offer:
- Full kitchens to prepare meals and save on dining costs
- Free WiFi for staying connected
- Washer/dryer for packing light
- Self check-in for ultimate flexibility

### Perfect for First-Time DC Visitors

If this is your first trip to Washington DC, staying near the National Mall ensures you won't miss any must-see attractions. Our central location makes it easy to:
- Visit multiple museums in a single day
- Walk the entire length of the Mall
- Explore monuments during less crowded evening hours
- Easily access other DC neighborhoods via Metro

### Book Your National Mall Vacation Rental

Make the most of your Washington DC vacation with a Capitol View Stays rental near the National Mall. Browse our properties today and book directly to save 15%.
    `,
    nearbyLocations: ["capitol-hill", "smithsonian", "washington-monument", "lincoln-memorial"]
  },
  {
    slug: "smithsonian",
    title: "Vacation Rentals Near Smithsonian Museums",
    metaTitle: "Vacation Rentals Near Smithsonian Museums | Capitol View Stays",
    metaDescription: "Book vacation rentals near Smithsonian museums in Washington DC. Walk to 19 free museums! Family-friendly homes with full kitchens. Save 15% booking direct.",
    keywords: "vacation rentals near Smithsonian, Smithsonian DC rentals, family vacation rental Washington DC",
    heroImage: "https://images.unsplash.com/photo-1558210834-2ccdb75f07a0?w=1200",
    intro: "Planning a trip to explore the world-famous Smithsonian museums? Capitol View Stays offers premium vacation rentals within walking distance of all 19 Smithsonian museums and galleries. Our family-friendly properties provide the space and amenities you need for an unforgettable museum-hopping adventure.",
    attractions: [
      { name: "National Air and Space Museum", distance: "0.5 miles", description: "Home to the Wright Flyer, Apollo 11 command module, and more aviation history." },
      { name: "National Museum of Natural History", distance: "0.6 miles", description: "Featuring the Hope Diamond and incredible dinosaur exhibits." },
      { name: "National Museum of American History", distance: "0.7 miles", description: "See the Star-Spangled Banner and First Ladies' gowns." },
      { name: "African American History Museum", distance: "0.8 miles", description: "The newest and most-visited Smithsonian museum." },
      { name: "National Zoo", distance: "3 miles", description: "Home to giant pandas and 2,700 animals from 390 species." }
    ],
    whyStay: [
      "Walk to all 19 Smithsonian museums and galleries",
      "Perfect for families with kids who love museums",
      "All Smithsonian museums are FREE admission",
      "Spacious rentals with room for the whole family",
      "Full kitchens save money on dining out"
    ],
    content: `
## The Ultimate Smithsonian Vacation Experience

The Smithsonian Institution is the world's largest museum and research complex, and it's entirely free to visit. With 19 museums and galleries to explore, you'll want a comfortable home base that puts you within easy reach of all these incredible attractions.

### 19 Museums, Zero Admission Fees

When you stay with Capitol View Stays near the Smithsonian museums, you're steps away from:

**On the National Mall:**
- National Air and Space Museum
- National Museum of Natural History
- National Museum of American History
- National Museum of African American History and Culture
- National Gallery of Art
- Freer Gallery of Art
- Arthur M. Sackler Gallery
- Hirshhorn Museum and Sculpture Garden
- National Museum of African Art
- Arts and Industries Building

**Nearby:**
- National Zoo (free!)
- National Portrait Gallery
- Smithsonian American Art Museum
- Renwick Gallery
- National Postal Museum

### Why Families Love Our Smithsonian-Area Rentals

Exploring museums with kids requires energy—and lots of snacks! Our vacation rentals near the Smithsonian offer:

**Full Kitchens:** Pack lunches and snacks to fuel your museum adventures. Save hundreds on dining costs compared to eating out for every meal.

**Multiple Bedrooms:** Give everyone their own space to rest after a day of exploration. Our properties sleep 6-11 guests comfortably.

**Laundry Facilities:** Pack light knowing you can wash clothes during your stay. Perfect for families with young children!

**High-Speed WiFi:** Keep kids entertained in the evenings and let adults catch up on work.

### Plan Your Smithsonian Adventure

With so many museums to choose from, we recommend spending at least 3-4 days to see the highlights. Our location near the Smithsonian makes it easy to:

- Visit 2-3 museums per day without exhausting travel
- Return to your rental for lunch and rest breaks
- Explore museums during less crowded morning and evening hours

### Book Direct and Save 15%

Ready to plan your Smithsonian vacation? Book directly with Capitol View Stays and save 15% compared to third-party sites. Our family-friendly vacation rentals provide everything you need for an unforgettable DC museum experience.
    `,
    nearbyLocations: ["national-mall", "capitol-hill", "national-zoo", "dupont-circle"]
  },
  {
    slug: "eastern-market",
    title: "Vacation Rentals Near Eastern Market",
    metaTitle: "Vacation Rentals Near Eastern Market DC | Capitol View Stays",
    metaDescription: "Stay near Eastern Market in Washington DC. Premium vacation rentals in Capitol Hill's vibrant heart. Local dining, shopping & culture. Save 15% booking direct!",
    keywords: "Eastern Market DC vacation rental, Capitol Hill rentals, vacation rentals near Eastern Market",
    heroImage: "https://images.unsplash.com/photo-1533900298318-6b8da08a523e?w=1200",
    intro: "Discover the heart of Capitol Hill with a vacation rental near Eastern Market. Capitol View Stays offers premium accommodations steps from DC's most beloved neighborhood market, surrounded by local restaurants, boutiques, and authentic Washington culture.",
    attractions: [
      { name: "Eastern Market", distance: "0.2 miles", description: "Historic market with vendors, fresh food, and weekend flea market." },
      { name: "Barracks Row", distance: "0.3 miles", description: "DC's oldest commercial corridor with restaurants and shops." },
      { name: "Capitol Hill Books", distance: "0.2 miles", description: "Iconic independent bookstore beloved by locals." },
      { name: "Lincoln Park", distance: "0.4 miles", description: "Beautiful neighborhood park perfect for morning walks." },
      { name: "Hill Center", distance: "0.3 miles", description: "Historic building hosting community events and programs." }
    ],
    whyStay: [
      "Experience authentic DC neighborhood life",
      "Walk to weekend farmers market and artisan vendors",
      "Dozens of restaurants and cafes within blocks",
      "Easy Metro access at Eastern Market station",
      "Perfect blend of local charm and visitor convenience"
    ],
    content: `
## Experience Authentic Washington DC at Eastern Market

Eastern Market isn't just a place—it's the soul of Capitol Hill. When you stay with Capitol View Stays near Eastern Market, you're immersing yourself in one of Washington DC's most authentic and beloved neighborhoods.

### A Market Like No Other

Since 1873, Eastern Market has served as the gathering place for Capitol Hill residents. Today, it offers:

**Indoor Market Hall (Tuesday-Sunday):**
- Fresh produce from local farms
- Artisan meats and cheeses
- Fresh-baked breads and pastries
- Flowers and plants

**Weekend Outdoor Markets:**
- Saturday: Farmers' market with local vendors
- Sunday: Flea market with antiques, art, and crafts

### Dine Like a Local on Barracks Row

Barracks Row (8th Street SE) is DC's oldest commercial corridor, now home to some of the neighborhood's best restaurants:

- **Ted's Bulletin:** Famous for homemade Pop-Tarts and breakfast
- **Belga Café:** Belgian cuisine and impressive beer selection
- **The Roost:** Food hall with multiple vendors
- **Rose's Luxury:** One of DC's most acclaimed restaurants

When you stay with Capitol View Stays, these culinary treasures are just a short walk from your door.

### The Perfect Neighborhood Rental

Our Eastern Market vacation rentals offer the best of both worlds: authentic neighborhood living with easy access to DC's major attractions. You'll enjoy:

- Walking distance to Eastern Market Metro (Orange, Blue, Silver lines)
- Quick access to Capitol Hill landmarks
- Local coffee shops and bakeries for morning routines
- Evening entertainment without leaving the neighborhood

### Book Your Eastern Market Getaway

Experience Washington DC the way locals do. Book directly with Capitol View Stays and save 15% on your Eastern Market vacation rental.
    `,
    nearbyLocations: ["capitol-hill", "navy-yard", "barracks-row", "national-mall"]
  },
  {
    slug: "union-station",
    title: "Vacation Rentals Near Union Station",
    metaTitle: "Vacation Rentals Near Union Station DC | Capitol View Stays",
    metaDescription: "Book vacation rentals near Union Station in Washington DC. Perfect for train travelers! Walk to Amtrak, Metro & Capitol Hill. Save 15% when you book direct.",
    keywords: "Union Station DC vacation rental, rentals near Union Station, Washington DC train station rentals",
    heroImage: "https://images.unsplash.com/photo-1568515387631-8b650bbcdb90?w=1200",
    intro: "Arriving by train? Capitol View Stays offers premium vacation rentals near Union Station, Washington DC's magnificent Beaux-Arts train station. Walk from Amtrak to your fully-furnished rental and start exploring the nation's capital immediately.",
    attractions: [
      { name: "Union Station", distance: "0.4 miles", description: "Stunning Beaux-Arts station serving Amtrak, Metro, and commuter rail." },
      { name: "U.S. Capitol", distance: "0.5 miles", description: "America's iconic legislative building, free to tour." },
      { name: "National Postal Museum", distance: "0.3 miles", description: "Free Smithsonian museum with fascinating postal history." },
      { name: "Columbus Circle", distance: "0.4 miles", description: "Beautiful fountain plaza in front of Union Station." },
      { name: "Senate Office Buildings", distance: "0.4 miles", description: "Watch democracy in action with free gallery passes." }
    ],
    whyStay: [
      "Walk from Amtrak directly to your rental",
      "Multiple Metro lines at Union Station",
      "Skip expensive airport transfers",
      "Perfect for Northeast Corridor travelers",
      "Central location for all DC attractions"
    ],
    content: `
## The Smart Choice for Train Travelers

Washington DC's Union Station is one of America's busiest and most beautiful train stations, serving over 40 million visitors annually. When you stay with Capitol View Stays near Union Station, you eliminate the hassle and expense of airport transfers while positioning yourself perfectly for DC exploration.

### Arrive by Train, Walk to Your Rental

Our vacation rentals near Union Station make arrival seamless:

1. Step off your Amtrak train
2. Walk through the magnificent Beaux-Arts station
3. Stroll to your fully-furnished Capitol View Stays rental
4. Start exploring Washington DC immediately

No taxis, no rideshares, no luggage hassles—just an easy walk to your home away from home.

### Union Station: More Than Just Trains

Union Station is a destination in itself, offering:
- **Shopping:** Over 70 stores and boutiques
- **Dining:** 40+ restaurants and food vendors
- **Architecture:** Stunning barrel-vaulted ceilings and classical details
- **History:** Opened in 1907, restored in the 1980s

### Perfect for Business Travelers

If you're traveling to DC for work, our Union Station-area rentals offer:
- Easy Amtrak access for day trips to NYC, Philadelphia, or Baltimore
- Dedicated workspaces with high-speed WiFi
- Full kitchens for preparing meals
- Quiet residential streets for peaceful rest

### Transportation Hub

Union Station connects you to everywhere:
- **Amtrak:** Northeast Corridor and long-distance routes
- **Metro:** Red Line hub for citywide access
- **MARC:** Maryland commuter rail
- **VRE:** Virginia commuter rail
- **Bus:** Multiple Metrobus and tour bus routes

### Book Your Union Station Vacation Rental

Traveling by train? Book directly with Capitol View Stays and save 15%. Our Union Station-area rentals provide the convenient, comfortable home base you need for your DC adventure.
    `,
    nearbyLocations: ["capitol-hill", "national-mall", "noma", "h-street"]
  },
  {
    slug: "navy-yard",
    title: "Vacation Rentals Near Navy Yard",
    metaTitle: "Vacation Rentals Near Navy Yard DC | Capitol View Stays",
    metaDescription: "Stay near Navy Yard in Washington DC. Premium vacation rentals near Nationals Park, The Wharf & waterfront dining. Perfect for baseball fans! Save 15% direct.",
    keywords: "Navy Yard DC vacation rental, rentals near Nationals Park, Washington DC waterfront rentals",
    heroImage: "https://images.unsplash.com/photo-1566024287286-457247b70310?w=1200",
    intro: "Experience Washington DC's vibrant waterfront with a vacation rental near Navy Yard. Capitol View Stays offers premium accommodations close to Nationals Park, The Yards, and DC's most exciting new neighborhood. Perfect for baseball fans, foodies, and anyone seeking a modern DC experience.",
    attractions: [
      { name: "Nationals Park", distance: "0.6 miles", description: "Home of the Washington Nationals MLB team." },
      { name: "The Yards", distance: "0.5 miles", description: "Waterfront dining, shopping, and entertainment district." },
      { name: "Yards Park", distance: "0.5 miles", description: "Beautiful riverside park with seasonal activities." },
      { name: "Navy Yard Metro", distance: "0.4 miles", description: "Green Line access to downtown and beyond." },
      { name: "Anacostia Riverwalk Trail", distance: "0.5 miles", description: "Scenic trail perfect for walking, running, or biking." }
    ],
    whyStay: [
      "Walk to Nationals Park for baseball games",
      "DC's hottest new restaurants and bars",
      "Beautiful waterfront parks and trails",
      "Modern, safe neighborhood with easy Metro access",
      "Perfect for sports fans and food lovers"
    ],
    content: `
## Washington DC's Most Exciting Neighborhood

Navy Yard has transformed from a historic naval facility into one of DC's most vibrant neighborhoods. When you stay with Capitol View Stays near Navy Yard, you're in the heart of waterfront dining, world-class sports, and exciting nightlife.

### Baseball Fans Welcome

Our Navy Yard vacation rentals are perfect for catching a Washington Nationals game:
- Walk to Nationals Park in under 10 minutes
- Skip traffic and parking headaches
- Pre-game at nearby bars and restaurants
- Return to your rental after night games easily

### Waterfront Dining & Entertainment

The Yards and surrounding area offer incredible dining options:
- **Bluejacket:** Award-winning brewery with innovative beers
- **Ice Cream Jubilee:** DC's best ice cream shop
- **All-Purpose Pizzeria:** Perfect for families
- **Whaley's:** Fresh seafood with river views

### More Than Baseball

Even when the Nationals aren't playing, Navy Yard offers:
- **Yards Park:** Outdoor movies, yoga, and seasonal events
- **Anacostia Riverwalk:** Miles of scenic waterfront trails
- **The Wharf:** Just a water taxi ride away
- **Capitol Riverfront:** Constantly evolving entertainment

### Book Your Navy Yard Vacation Rental

Ready for a waterfront DC experience? Book directly with Capitol View Stays and save 15% on your Navy Yard-area vacation rental.
    `,
    nearbyLocations: ["capitol-hill", "the-wharf", "eastern-market", "anacostia"]
  },
  {
    slug: "the-wharf",
    title: "Vacation Rentals Near The Wharf DC",
    metaTitle: "Vacation Rentals Near The Wharf DC | Capitol View Stays",
    metaDescription: "Book vacation rentals near The Wharf in Washington DC. Waterfront dining, concerts & entertainment steps away. Premium accommodations. Save 15% booking direct!",
    keywords: "The Wharf DC vacation rental, DC waterfront rentals, vacation rentals near The Wharf",
    heroImage: "https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?w=1200",
    intro: "Experience DC's premier waterfront destination with a vacation rental near The Wharf. Capitol View Stays offers premium accommodations close to world-class restaurants, live music venues, and stunning Potomac River views.",
    attractions: [
      { name: "The Wharf", distance: "0.8 miles", description: "DC's premier waterfront dining and entertainment district." },
      { name: "The Anthem", distance: "0.8 miles", description: "World-class concert venue hosting major artists." },
      { name: "Pearl Street Warehouse", distance: "0.8 miles", description: "Intimate live music venue." },
      { name: "Fish Market", distance: "0.9 miles", description: "Historic open-air seafood market." },
      { name: "Tidal Basin", distance: "1 mile", description: "Famous for cherry blossoms and Jefferson Memorial." }
    ],
    whyStay: [
      "DC's best waterfront restaurants and bars",
      "World-class concert venues within walking distance",
      "Beautiful Potomac River views",
      "Easy access to monuments via water taxi",
      "Vibrant nightlife and entertainment"
    ],
    content: `
## DC's Premier Waterfront Experience

The Wharf is Washington DC's most exciting waterfront development, offering over a mile of restaurants, shops, and entertainment venues along the Potomac River. When you stay with Capitol View Stays near The Wharf, you're steps from unforgettable dining and world-class entertainment.

### Waterfront Dining Excellence

The Wharf boasts some of DC's finest restaurants:
- **Del Mar:** Spanish seafood with stunning views
- **Officina:** Italian market and rooftop bar
- **Kith/Kin:** Acclaimed African diaspora cuisine
- **Hank's Oyster Bar:** Fresh seafood favorites

### Live Music & Entertainment

The Wharf is DC's live music epicenter:
- **The Anthem:** 6,000-seat venue hosting major tours
- **Pearl Street Warehouse:** Intimate shows in a converted warehouse
- **District Pier:** Outdoor concerts and events

### Beyond Dining and Music

The Wharf offers endless activities:
- Water taxi to Georgetown and Alexandria
- Paddleboarding and kayaking
- Seasonal festivals and markets
- Stunning sunset views over the Potomac

### Book Your Wharf-Area Vacation Rental

Experience DC's waterfront lifestyle. Book directly with Capitol View Stays and save 15% on your stay near The Wharf.
    `,
    nearbyLocations: ["tidal-basin", "national-mall", "navy-yard", "jefferson-memorial"]
  },
  {
    slug: "lincoln-memorial",
    title: "Vacation Rentals Near Lincoln Memorial",
    metaTitle: "Vacation Rentals Near Lincoln Memorial | Capitol View Stays",
    metaDescription: "Stay near the Lincoln Memorial in Washington DC. Premium vacation rentals close to America's most iconic monument. Walk to memorials. Save 15% booking direct!",
    keywords: "Lincoln Memorial vacation rental, rentals near Lincoln Memorial DC, National Mall rentals",
    heroImage: "https://images.unsplash.com/photo-1585511582728-6afe7ca41319?w=1200",
    intro: "Experience America's most moving monument with a vacation rental near the Lincoln Memorial. Capitol View Stays offers premium accommodations within walking distance of this iconic tribute to our 16th president and the surrounding memorials.",
    attractions: [
      { name: "Lincoln Memorial", distance: "1 mile", description: "Majestic marble memorial featuring the seated Lincoln statue." },
      { name: "Vietnam Veterans Memorial", distance: "0.9 miles", description: "The powerful black granite wall honoring Vietnam War casualties." },
      { name: "Korean War Veterans Memorial", distance: "0.9 miles", description: "Haunting statues depicting soldiers on patrol." },
      { name: "World War II Memorial", distance: "0.8 miles", description: "Grand memorial with fountains and state pillars." },
      { name: "Reflecting Pool", distance: "0.8 miles", description: "Iconic pool stretching from Lincoln Memorial to WWII Memorial." }
    ],
    whyStay: [
      "Walk to the Lincoln Memorial for sunrise or sunset",
      "Experience the memorials without crowds at night",
      "Central location for all National Mall attractions",
      "Perfect for history buffs and families",
      "Unforgettable views of the Reflecting Pool"
    ],
    content: `
## Walk in History's Footsteps

The Lincoln Memorial stands as one of America's most powerful symbols of unity and freedom. When you stay with Capitol View Stays near the Lincoln Memorial, you can experience this magnificent monument during the magical hours of sunrise and sunset, when crowds thin and history comes alive.

### More Than a Monument

The Lincoln Memorial was the site of:
- Martin Luther King Jr.'s "I Have a Dream" speech (1963)
- Marian Anderson's historic concert (1939)
- Countless protests, celebrations, and gatherings

### The Perfect Evening Walk

Our guests love ending their days with a walk to the Lincoln Memorial:
- Watch the sunset behind the memorial
- See the Reflecting Pool mirror the Washington Monument
- Experience the monument illuminated at night
- Avoid daytime crowds and heat

### Surrounding Memorials

The western end of the National Mall offers a powerful concentration of memorials:
- Vietnam Veterans Memorial
- Korean War Veterans Memorial
- World War II Memorial
- Martin Luther King Jr. Memorial
- FDR Memorial

### Book Your Lincoln Memorial Vacation Rental

Honor America's history with a stay near the Lincoln Memorial. Book directly with Capitol View Stays and save 15%.
    `,
    nearbyLocations: ["national-mall", "tidal-basin", "washington-monument", "the-wharf"]
  },
  {
    slug: "washington-monument",
    title: "Vacation Rentals Near Washington Monument",
    metaTitle: "Vacation Rentals Near Washington Monument DC | Capitol View Stays",
    metaDescription: "Book vacation rentals near the Washington Monument in DC. Stay at the heart of the National Mall. Premium accommodations near iconic landmarks. Save 15%!",
    keywords: "Washington Monument vacation rental, National Mall DC rentals, vacation rentals near monuments",
    heroImage: "https://images.unsplash.com/photo-1617581629397-a72507c3de9e?w=1200",
    intro: "Stay at the center of America with a vacation rental near the Washington Monument. Capitol View Stays offers premium accommodations within walking distance of DC's most iconic landmark and the museums that surround it.",
    attractions: [
      { name: "Washington Monument", distance: "0.7 miles", description: "The 555-foot marble obelisk honoring George Washington." },
      { name: "National Museum of American History", distance: "0.6 miles", description: "Home to the Star-Spangled Banner and First Ladies' gowns." },
      { name: "National Museum of Natural History", distance: "0.5 miles", description: "The Hope Diamond and incredible dinosaur exhibits." },
      { name: "White House", distance: "1 mile", description: "The President's residence, visible from the monument grounds." },
      { name: "World War II Memorial", distance: "0.6 miles", description: "Stunning memorial with fountains and state pillars." }
    ],
    whyStay: [
      "Central location on the National Mall",
      "Walk to all major monuments and museums",
      "Perfect starting point for DC exploration",
      "Iconic views from every direction",
      "Easy Metro access nearby"
    ],
    content: `
## The Heart of Washington DC

The Washington Monument stands at the center of the National Mall, offering panoramic views of the Capitol, White House, and Lincoln Memorial. When you stay with Capitol View Stays near the Washington Monument, you're perfectly positioned to explore everything the National Mall offers.

### Central to Everything

From the Washington Monument, you can easily reach:
- **East:** U.S. Capitol, Library of Congress, Supreme Court
- **West:** Lincoln Memorial, Vietnam Veterans Memorial, Reflecting Pool
- **North:** White House, museums, downtown DC
- **South:** Tidal Basin, Jefferson Memorial, MLK Memorial

### Monument Tours

Free timed tickets allow you to ride to the top of the Washington Monument for unmatched views:
- Reserve tickets online in advance
- Best views at sunrise or sunset
- See all of DC from 500 feet up

### The Perfect Photo Opportunity

Our guests love capturing the Washington Monument:
- Sunrise shots with the Capitol
- Sunset silhouettes
- Night illumination
- Reflection Pool mirrors

### Book Your Washington Monument Vacation Rental

Stay at the center of American history. Book directly with Capitol View Stays and save 15% on your National Mall vacation rental.
    `,
    nearbyLocations: ["national-mall", "lincoln-memorial", "smithsonian", "white-house"]
  },
  {
    slug: "national-zoo",
    title: "Vacation Rentals Near National Zoo",
    metaTitle: "Vacation Rentals Near National Zoo DC | Capitol View Stays",
    metaDescription: "Book family-friendly vacation rentals near the National Zoo in Washington DC. See giant pandas & 2,700 animals! Free admission. Save 15% booking direct!",
    keywords: "National Zoo vacation rental, DC zoo rentals, family vacation rental Washington DC",
    heroImage: "https://images.unsplash.com/photo-1534567153574-2b12153a87f0?w=1200",
    intro: "Plan the perfect family vacation with a rental near the Smithsonian's National Zoo. Capitol View Stays offers family-friendly accommodations close to one of America's oldest and most beloved zoos—and admission is always free!",
    attractions: [
      { name: "Smithsonian National Zoo", distance: "2 miles", description: "Home to 2,700 animals including giant pandas. Free admission!" },
      { name: "Rock Creek Park", distance: "2 miles", description: "1,754 acres of urban wilderness perfect for hiking." },
      { name: "Woodley Park", distance: "2 miles", description: "Charming neighborhood with restaurants and shops." },
      { name: "Adams Morgan", distance: "2.5 miles", description: "Vibrant neighborhood known for nightlife and diverse dining." },
      { name: "Cleveland Park", distance: "2.5 miles", description: "Historic neighborhood with movie theaters and cafes." }
    ],
    whyStay: [
      "Free admission to the National Zoo",
      "Giant pandas and 2,700+ animals",
      "Perfect for families with children",
      "Nearby hiking in Rock Creek Park",
      "Multiple visits possible during your stay"
    ],
    content: `
## America's Favorite Free Zoo

The Smithsonian's National Zoo is one of the oldest zoos in the United States and one of the few with free admission. When you stay with Capitol View Stays, you can visit as many times as you like during your DC vacation.

### Giant Pandas and More

The National Zoo is home to:
- **Giant Pandas:** The zoo's most famous residents
- **Asian Elephants:** Including the Elephant Trails habitat
- **Great Cats:** Lions, tigers, and other big cats
- **Amazonia:** Tropical rainforest exhibit
- **Think Tank:** Orangutans demonstrating intelligence

### Tips for Zoo Visits

Our guests recommend:
- Arrive early when animals are most active
- Visit on weekdays to avoid crowds
- Plan multiple shorter visits rather than one long day
- Bring lunch—picnic areas available throughout

### Explore the Neighborhood

The zoo is surrounded by great DC neighborhoods:
- **Woodley Park:** Cafes and restaurants near the zoo entrance
- **Adams Morgan:** DC's most diverse dining scene
- **Cleveland Park:** Art deco theater and local shops

### Book Your National Zoo Vacation Rental

Make memories with the whole family. Book directly with Capitol View Stays and save 15% on your DC family vacation rental.
    `,
    nearbyLocations: ["adams-morgan", "dupont-circle", "woodley-park", "rock-creek-park"]
  },
  {
    slug: "georgetown",
    title: "Vacation Rentals Near Georgetown DC",
    metaTitle: "Vacation Rentals Near Georgetown DC | Capitol View Stays",
    metaDescription: "Book vacation rentals near Georgetown in Washington DC. Historic charm, waterfront dining, boutique shopping & cobblestone streets. Save 15% booking direct!",
    keywords: "Georgetown DC vacation rental, rentals near Georgetown, Washington DC historic neighborhood",
    heroImage: "https://images.unsplash.com/photo-1594395244228-3d17e6ec4705?w=1200",
    intro: "Experience Washington DC's most charming neighborhood with a vacation rental near Georgetown. Capitol View Stays offers accommodations with easy access to Georgetown's historic cobblestone streets, waterfront, boutiques, and renowned restaurants.",
    attractions: [
      { name: "Georgetown Waterfront", distance: "1.5 miles", description: "Beautiful riverfront park with dining and walking paths." },
      { name: "M Street Shopping", distance: "1.5 miles", description: "Boutiques, national brands, and unique local shops." },
      { name: "Georgetown University", distance: "2 miles", description: "Historic Jesuit university with beautiful campus." },
      { name: "C&O Canal", distance: "1.5 miles", description: "Historic canal with towpath perfect for walks and biking." },
      { name: "Dumbarton Oaks", distance: "2 miles", description: "Beautiful gardens and Byzantine art museum." }
    ],
    whyStay: [
      "DC's most historic and charming neighborhood",
      "World-class shopping and dining",
      "Beautiful waterfront parks and trails",
      "Easy access via water taxi",
      "Unique boutiques you won't find elsewhere"
    ],
    content: `
## Washington DC's Most Charming Neighborhood

Georgetown predates Washington DC itself, founded in 1751. Today, it's known for cobblestone streets, Federal-era architecture, world-class dining, and boutique shopping. Stay with Capitol View Stays for easy access to this beloved neighborhood.

### Shopping on M Street

Georgetown offers shopping you won't find anywhere else:
- Designer boutiques and national brands
- Antique shops and galleries
- Book stores and specialty shops
- Unique DC souvenirs

### Waterfront Dining & Recreation

The Georgetown Waterfront offers:
- Riverside restaurants with sunset views
- Walking and biking trails
- Kayaking and paddleboarding
- Water taxi to The Wharf and Alexandria

### Historic Charm

Explore Georgetown's history:
- Pre-Revolutionary War architecture
- Georgetown University campus
- Dumbarton Oaks gardens
- Tudor Place historic house

### Book Your Georgetown Vacation Rental

Experience DC's most charming neighborhood. Book directly with Capitol View Stays and save 15%.
    `,
    nearbyLocations: ["dupont-circle", "foggy-bottom", "the-wharf", "rosslyn"]
  },
  {
    slug: "dupont-circle",
    title: "Vacation Rentals Near Dupont Circle",
    metaTitle: "Vacation Rentals Near Dupont Circle DC | Capitol View Stays",
    metaDescription: "Book vacation rentals near Dupont Circle in Washington DC. Embassy Row, art galleries, cafes & vibrant nightlife. Central location. Save 15% booking direct!",
    keywords: "Dupont Circle vacation rental, DC Embassy Row rentals, Dupont Circle DC vacation",
    heroImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200",
    intro: "Stay in Washington DC's most vibrant neighborhood with a vacation rental near Dupont Circle. Capitol View Stays offers accommodations with easy access to Embassy Row, art galleries, independent bookstores, and DC's best coffee shops.",
    attractions: [
      { name: "Dupont Circle Park", distance: "1 mile", description: "Iconic fountain and gathering place at the neighborhood's heart." },
      { name: "Embassy Row", distance: "1 mile", description: "Massachusetts Avenue lined with embassies and mansions." },
      { name: "Phillips Collection", distance: "1 mile", description: "America's first modern art museum." },
      { name: "Kramerbooks", distance: "1 mile", description: "Iconic bookstore and cafe open late." },
      { name: "Connecticut Avenue", distance: "1 mile", description: "Shops, restaurants, and galleries." }
    ],
    whyStay: [
      "DC's most walkable neighborhood",
      "World-class art galleries and museums",
      "Incredible restaurant scene",
      "Historic mansions and Embassy Row",
      "Vibrant cafe culture"
    ],
    content: `
## Washington DC's Cultural Heart

Dupont Circle is where DC's intellectual and artistic communities thrive. When you stay with Capitol View Stays near Dupont Circle, you're surrounded by art galleries, independent bookstores, diverse restaurants, and the beautiful mansions of Embassy Row.

### Art and Culture

Dupont Circle offers incredible cultural experiences:
- **Phillips Collection:** America's first modern art museum
- **Studio galleries:** Dozens of small galleries throughout
- **Embassy tours:** Many embassies offer public tours
- **Independent bookstores:** Kramerbooks, Second Story Books, and more

### Embassy Row

Massachusetts Avenue is home to dozens of embassies, offering:
- Beautiful architecture to admire
- Cultural festivals and events
- International dining options
- Glimpses of diplomatic life

### Cafe Culture

Dupont Circle is DC's coffee capital:
- Independent coffee roasters
- Outdoor cafes perfect for people-watching
- Late-night bookstore cafes
- Brunch institutions

### Book Your Dupont Circle Vacation Rental

Immerse yourself in DC culture. Book directly with Capitol View Stays and save 15%.
    `,
    nearbyLocations: ["logan-circle", "adams-morgan", "foggy-bottom", "u-street"]
  },
  {
    slug: "logan-circle",
    title: "Vacation Rentals Near Logan Circle",
    metaTitle: "Vacation Rentals Near Logan Circle DC | Capitol View Stays",
    metaDescription: "Book vacation rentals near Logan Circle in Washington DC. Victorian architecture, trendy restaurants & bars. Walkable neighborhood. Save 15% booking direct!",
    keywords: "Logan Circle DC vacation rental, Logan Circle rentals, Washington DC Victorian neighborhood",
    heroImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200",
    intro: "Experience one of DC's most beautiful neighborhoods with a vacation rental near Logan Circle. Capitol View Stays offers accommodations surrounded by stunning Victorian rowhouses, acclaimed restaurants, and a thriving local community.",
    attractions: [
      { name: "Logan Circle Park", distance: "1 mile", description: "Beautiful traffic circle surrounded by Victorian mansions." },
      { name: "14th Street Corridor", distance: "0.8 miles", description: "DC's hottest restaurant and bar scene." },
      { name: "Studio Theatre", distance: "1 mile", description: "Award-winning contemporary theater." },
      { name: "Mary McLeod Bethune House", distance: "1 mile", description: "Historic home of the civil rights leader." },
      { name: "P Street restaurants", distance: "0.9 miles", description: "Acclaimed dining destinations." }
    ],
    whyStay: [
      "Stunning Victorian architecture",
      "DC's best restaurant scene on 14th Street",
      "Walkable to downtown and Dupont",
      "Beautiful historic neighborhood",
      "Vibrant nightlife options"
    ],
    content: `
## DC's Most Beautiful Neighborhood

Logan Circle features some of Washington DC's finest Victorian architecture, surrounding a beautiful fountain-centered traffic circle. Today, it's known for an incredible dining scene that rivals any neighborhood in America.

### 14th Street Restaurant Row

The 14th Street corridor offers:
- **Le Diplomate:** DC's favorite French bistro
- **Barcelona:** Spanish tapas and wine
- **Estadio:** Spanish cuisine and extensive sherry menu
- **Pearl Dive Oyster Palace:** Fresh seafood favorites

### Victorian Grandeur

Logan Circle's architecture includes:
- Ornate Victorian rowhouses
- Historic churches
- Beautifully preserved mansions
- Tree-lined streets

### Central Location

Logan Circle offers easy access to:
- Downtown DC and Metro Center
- Dupont Circle neighborhood
- U Street music corridor
- Convention Center

### Book Your Logan Circle Vacation Rental

Stay in DC's most beautiful neighborhood. Book directly with Capitol View Stays and save 15%.
    `,
    nearbyLocations: ["dupont-circle", "u-street", "shaw", "downtown"]
  },
  {
    slug: "adams-morgan",
    title: "Vacation Rentals Near Adams Morgan",
    metaTitle: "Vacation Rentals Near Adams Morgan DC | Capitol View Stays",
    metaDescription: "Book vacation rentals near Adams Morgan in Washington DC. Diverse dining, vibrant nightlife & cultural hub. DC's most eclectic neighborhood. Save 15% direct!",
    keywords: "Adams Morgan DC vacation rental, Adams Morgan rentals, Washington DC nightlife rentals",
    heroImage: "https://images.unsplash.com/photo-1570655652364-2e0a67455ac6?w=1200",
    intro: "Experience Washington DC's most diverse and vibrant neighborhood with a vacation rental near Adams Morgan. Capitol View Stays offers accommodations close to incredible international cuisine, legendary nightlife, and a colorful mix of cultures.",
    attractions: [
      { name: "18th Street", distance: "2 miles", description: "Main strip with restaurants from around the world." },
      { name: "Adams Morgan Day Festival", distance: "2 miles", description: "Annual September festival celebrating the neighborhood." },
      { name: "Meridian Hill Park", distance: "1.5 miles", description: "Beautiful cascading fountain and Sunday drum circles." },
      { name: "International Restaurants", distance: "2 miles", description: "Ethiopian, Salvadoran, Vietnamese, and more." },
      { name: "Live Music Venues", distance: "2 miles", description: "Intimate clubs and legendary dive bars." }
    ],
    whyStay: [
      "DC's most diverse dining scene",
      "Legendary nightlife and music venues",
      "Cultural events year-round",
      "International restaurants from dozens of countries",
      "Authentic DC neighborhood experience"
    ],
    content: `
## Washington DC's Most Eclectic Neighborhood

Adams Morgan is where DC comes to celebrate diversity. Named by combining neighboring Black (Adams) and white (Morgan) elementary schools during integration, the neighborhood embodies DC's multicultural spirit.

### International Food Capital

Adams Morgan offers cuisine from around the world:
- Ethiopian (DC has the largest Ethiopian population outside Africa)
- Salvadoran pupuserias
- Vietnamese pho shops
- Mexican taquerias
- And much more

### Legendary Nightlife

Adams Morgan has been DC's nightlife hub for decades:
- Live music venues for every taste
- Rooftop bars with city views
- Dive bars with character
- Late-night pizza joints

### Meridian Hill Park

One of DC's most beautiful parks offers:
- Cascading fountain inspired by Italian gardens
- Sunday afternoon drum circles
- Stunning city views
- Perfect picnic spots

### Book Your Adams Morgan Vacation Rental

Experience DC's diverse culture. Book directly with Capitol View Stays and save 15%.
    `,
    nearbyLocations: ["dupont-circle", "columbia-heights", "u-street", "woodley-park"]
  }
];

// Blog/Guide content
export const blogArticles = [
  {
    slug: "best-things-to-do-washington-dc-capitol-hill",
    title: "Best Things to Do in Washington DC Near Capitol Hill",
    metaTitle: "Best Things to Do Near Capitol Hill DC | Capitol View Stays",
    metaDescription: "Discover the best attractions, restaurants, and activities near Capitol Hill in Washington DC. Local tips from Capitol View Stays vacation rental experts.",
    category: "travel-guide",
    readTime: "8 min read",
    featured: true
  },
  {
    slug: "complete-washington-dc-visitor-guide",
    title: "The Complete Washington DC Visitor Guide 2025",
    metaTitle: "Complete Washington DC Visitor Guide 2025 | Capitol View Stays",
    metaDescription: "Everything you need to know for your Washington DC vacation. Museums, monuments, neighborhoods, dining and more. Your complete DC travel guide.",
    category: "travel-guide",
    readTime: "15 min read",
    featured: true
  },
  {
    slug: "where-to-stay-washington-dc-first-time",
    title: "Where to Stay in Washington DC for First-Time Visitors",
    metaTitle: "Where to Stay in DC First Time | Capitol View Stays",
    metaDescription: "First time visiting Washington DC? Learn which neighborhoods offer the best locations for tourists, with pros and cons of each area.",
    category: "travel-guide",
    readTime: "10 min read",
    featured: true
  },
  {
    slug: "3-day-washington-dc-itinerary",
    title: "The Perfect 3-Day Washington DC Itinerary",
    metaTitle: "3 Day DC Itinerary - What to Do | Capitol View Stays",
    metaDescription: "Plan your perfect 3-day Washington DC trip with our detailed itinerary covering monuments, museums, neighborhoods and local favorites.",
    category: "itinerary",
    readTime: "12 min read",
    featured: false
  },
  {
    slug: "best-neighborhoods-washington-dc",
    title: "Best Neighborhoods to Stay in Washington DC",
    metaTitle: "Best DC Neighborhoods for Tourists | Capitol View Stays",
    metaDescription: "Compare Washington DC neighborhoods to find the perfect area for your vacation. Capitol Hill, Dupont Circle, Georgetown and more.",
    category: "travel-guide",
    readTime: "11 min read",
    featured: false
  },
  {
    slug: "family-activities-washington-dc",
    title: "Top 25 Family Activities in Washington DC",
    metaTitle: "Best Family Activities in DC | Capitol View Stays",
    metaDescription: "Planning a family trip to Washington DC? Discover 25 kid-friendly activities, museums, and attractions perfect for all ages.",
    category: "family-travel",
    readTime: "14 min read",
    featured: true
  },
  {
    slug: "washington-dc-travel-tips",
    title: "Washington DC Travel Tips: What Tourists Need to Know",
    metaTitle: "DC Travel Tips for Tourists | Capitol View Stays",
    metaDescription: "Essential Washington DC travel tips including transportation, best times to visit, money-saving advice, and local secrets.",
    category: "travel-tips",
    readTime: "9 min read",
    featured: false
  }
];

// Resource pages for backlinks
export const resourcePages = [
  {
    slug: "complete-visitor-guide-washington-dc",
    title: "The Complete Visitor's Guide to Washington DC",
    metaTitle: "Complete DC Visitor Guide | Capitol View Stays",
    metaDescription: "The most comprehensive Washington DC visitor guide online. Everything you need to plan your perfect DC vacation.",
    wordCount: 3500
  },
  {
    slug: "washington-dc-monuments-museums-guide",
    title: "Ultimate Guide to Washington DC Monuments & Museums",
    metaTitle: "DC Monuments & Museums Guide | Capitol View Stays",
    metaDescription: "Complete guide to all Washington DC monuments, memorials, and Smithsonian museums. Free attractions, tips, and insider advice.",
    wordCount: 4000
  },
  {
    slug: "best-time-visit-washington-dc",
    title: "Best Time to Visit Washington DC: Month-by-Month Guide",
    metaTitle: "Best Time to Visit DC | Capitol View Stays",
    metaDescription: "Find the perfect time for your Washington DC vacation. Weather, crowds, events, and seasonal tips for every month of the year.",
    wordCount: 2500
  },
  {
    slug: "washington-dc-neighborhood-guide",
    title: "Washington DC Neighborhood Guide for Travelers",
    metaTitle: "DC Neighborhood Guide | Capitol View Stays",
    metaDescription: "Explore every Washington DC neighborhood. Find the perfect area for your trip with our comprehensive neighborhood guide.",
    wordCount: 3000
  }
];
